import React, { useState, useEffect, useRef } from 'react'
import { arrayOf, bool, func, oneOf, shape, string, oneOfType, number } from 'prop-types'
import classnames from 'classnames'

import Validator from 'utils/Validator'

const InputField = ({
  type,
  name,
  placeholder,
  handleChange,
  validations,
  isRequired,
  className,
  value,
  isTextarea,
  ...otherProps
}) => {
  const Element = isTextarea ? 'textarea' : 'input'
  const didMountRef = useRef(false)
  const prevInvalidRules = useRef([])
  const [inValidRules, setInvalidRules] = useState([])
  const handleOnChange = (e) => {
    e.preventDefault()
    handleChange && handleChange({
      name: e.target.name,
      value: e.target.value,
    })
  }

  useEffect(() => {
    const doValidation = () => {
      if (validations.length > 0) {
        const rules = validations.map(validation => validation.rule)
        if (prevInvalidRules !== inValidRules) {
          const validationResults = Validator.validate(value, rules)
          setInvalidRules(validationResults)
          prevInvalidRules.current = inValidRules
        }
      }
    }
    if (didMountRef.current) {
      doValidation()
    } else {
      didMountRef.current = true
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  return (
    <>
      <Element
        className={classnames('input-field', className, {
          'input-field--invalid': inValidRules.length > 0,
        })}
        type={type}
        name={name}
        placeholder={placeholder}
        onChange={handleOnChange}
        required={isRequired}
        value={value}
        {...otherProps}
      />
      {
        validations.map((validation, idx) => {
          const isBrokenRule = inValidRules.indexOf(validation.rule) >= 0
          if (isBrokenRule) {
            return (
              <p className="input-field__notification" key={`notif-${idx}`}>{validation.invalidText}</p>
            )
          }
          return null
        })
      }
      <style jsx>
        {`
          .input-field {
            font-size: 16px;
            color: #485c77;

            width: 100%;
            padding: .5rem .75rem;
            box-sizing: border-box;

            border-radius: .25rem;
            border: 1px solid #a3b4ca;
            box-shadow: inset 0 1px 1px rgba(0,0,0,0.075);

            &:focus, &:active {
              border: 1px solid #1e90ff;
              outline: #1e90ff;
              box-shadow: 0 0 3px #1e90ff;
            }
            &--invalid {
              border: solid 1px #fb627a !important;
              outline: #fb627a !important;
              box-shadow: 0 0 3px #fb627a !important;
            }
            &__notification {
              color: #fb627a;
            }
          }
        `}
      </style>
    </>
  )
}

InputField.propTypes = {
  className: string,
  type: oneOf(['text', 'tel', 'email', 'password', 'hidden', 'number']),
  name: string,
  placeholder: string,
  handleChange: func,
  value: oneOfType([string, number]),
  validations: arrayOf(shape({
    rule: oneOf([
      'required',
      'email',
      'username',
      'email_username',
      'phone_number',
      'contains_text_and_number',
      'min_length',
      'min_length_utbk',
    ]),
    invalidText: string,
  })),
  isRequired: bool,
  isTextarea: bool,
}

InputField.defaultProps = {
  type: 'text',
  name: '',
  placeholder: '',
  validations: [],
}

export default InputField
